import { type InputHTMLAttributes, forwardRef } from 'react';
import { cn } from '../utils';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, className, ...props }, ref) => (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="text-xs font-semibold text-content-muted dark:text-dark-400 uppercase tracking-wide">
          {label}
        </label>
      )}
      <input
        ref={ref}
        className={cn(
          'w-full h-8 px-3 text-sm rounded-lg border transition-all duration-150',
          'bg-gray-50 dark:bg-dark-900/80',
          'border-line dark:border-dark-600',
          'text-gray-800 dark:text-white placeholder-gray-300 dark:placeholder-dark-600',
          'focus:outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400 focus:bg-surface dark:focus:bg-dark-900',
          error && 'border-red-400 focus:ring-red-400/30',
          className
        )}
        {...props}
      />
      {error  && <span className="text-xs text-red-500 dark:text-red-400">{error}</span>}
      {hint && !error && <span className="text-xs text-content-subtle dark:text-dark-500">{hint}</span>}
    </div>
  )
);
Input.displayName = 'Input';

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, className, ...props }, ref) => (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="text-xs font-semibold text-content-muted dark:text-dark-400 uppercase tracking-wide">
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        className={cn(
          'w-full px-3 py-2 text-sm rounded-lg border resize-none transition-all duration-150',
          'bg-gray-50 dark:bg-dark-900/80',
          'border-line dark:border-dark-600',
          'text-gray-800 dark:text-white placeholder-gray-300 dark:placeholder-dark-600',
          'focus:outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400 focus:bg-surface dark:focus:bg-dark-900',
          error && 'border-red-400',
          className
        )}
        {...props}
      />
      {error && <span className="text-xs text-red-500 dark:text-red-400">{error}</span>}
    </div>
  )
);
Textarea.displayName = 'Textarea';
